import {
	type BoundingSphere,
	type Camera,
	Cartesian2,
	Cartesian3,
	type Cesium3DTileset,
	Math as CesiumMath,
	Intersect,
	PerspectiveFrustum,
	type Viewer,
} from 'cesium';
import {
	boxBoundingSphere,
	type ClipBox,
	clusterAround,
	fitBox,
	isGeoreferenced,
	type LayerTileset,
	layerPieces,
	pointInBox,
	rayEntryDistance,
	tilesetVolume,
} from './clipping-box-geometry';

// A model this large on screen, as a share of the wider screen side, makes the tool available.
const MIN_MODEL_SCREEN_SHARE = 0.1;
// Layers at most this far apart (metres) form one model.
const TOUCH_TOLERANCE = 1;
// How many objects under the centre are looked through for the model.
const PICK_DEPTH = 5;

/** The camera's field of view in radians; other frustums have none, so they count as 60°. */
const viewFov = (camera: Camera) =>
	camera.frustum instanceof PerspectiveFrustum && camera.frustum.fov !== undefined
		? camera.frustum.fov
		: CesiumMath.toRadians(60);

const widerScreenSide = (viewer: Viewer) => Math.max(viewer.scene.canvas.clientWidth, viewer.scene.canvas.clientHeight);

const canvasCentre = (viewer: Viewer) =>
	new Cartesian2(viewer.scene.canvas.clientWidth / 2, viewer.scene.canvas.clientHeight / 2);

const inView = (viewer: Viewer, sphere: BoundingSphere) => {
	const { camera } = viewer;
	const culling = camera.frustum.computeCullingVolume(camera.positionWC, camera.directionWC, camera.upWC);
	return culling.computeVisibility(sphere) !== Intersect.OUTSIDE;
};

/** Pixels the sphere spans on screen; infinite with the camera inside it. */
const screenPixels = (viewer: Viewer, sphere: BoundingSphere) => {
	const { camera } = viewer;
	const gap = Cartesian3.distance(camera.positionWC, sphere.center) - sphere.radius;
	if (gap <= 0) return Number.POSITIVE_INFINITY;
	const metresPerPixel = (2 * gap * Math.tan(viewFov(camera) / 2)) / widerScreenSide(viewer);
	return (2 * sphere.radius) / metresPerPixel;
};

/** Layers a box can be placed on: shown, georeferenced and not Gaussian splats. */
export const boxCandidates = (tilesets: Cesium3DTileset[]) =>
	tilesets.filter(
		(tileset) =>
			// A replaced layer is destroyed before the viewer's layer list catches up; reading it throws.
			!tileset.isDestroyed() &&
			tileset.show &&
			!(tileset as LayerTileset).isSplat &&
			isGeoreferenced(tileset.boundingSphere.center),
	);

/** Whether a model in view is large enough on screen to place the limit box on. */
export const isLimitBoxAvailable = (viewer: Viewer, tilesets: Cesium3DTileset[]) => {
	const minPixels = widerScreenSide(viewer) * MIN_MODEL_SCREEN_SHARE;
	return boxCandidates(tilesets).some(
		({ boundingSphere }) => inView(viewer, boundingSphere) && screenPixels(viewer, boundingSphere) >= minPixels,
	);
};

/**
 * The first candidate among the objects under the centre of the view, which may be a damage,
 * a marker or base map tiles in front of the model; else the candidate in view nearest to the
 * centre ray.
 */
const findAnchor = (viewer: Viewer, candidates: Cesium3DTileset[]) => {
	// A tile feature carries `tileset` and `primitive`; plain tile content carries `primitive`.
	const hits = viewer.scene.drillPick(canvasCentre(viewer), PICK_DEPTH) as { primitive?: unknown; tileset?: unknown }[];
	for (const hit of hits) {
		const underCentre = candidates.find((tileset) => tileset === hit?.primitive || tileset === hit?.tileset);
		if (underCentre) return underCentre;
	}

	const visible = candidates.filter(({ boundingSphere }) => inView(viewer, boundingSphere));
	if (visible.length === 0) return undefined;
	const { camera } = viewer;
	const offRay = ({ boundingSphere }: Cesium3DTileset) => {
		const toCentre = Cartesian3.subtract(boundingSphere.center, camera.positionWC, new Cartesian3());
		const along = Cartesian3.dot(toCentre, camera.directionWC);
		const across = Math.sqrt(Math.max(Cartesian3.magnitudeSquared(toCentre) - along * along, 0));
		return Math.max(across - boundingSphere.radius, 0);
	};
	return visible.reduce((best, tileset) => (offRay(tileset) < offRay(best) ? tileset : best));
};

/**
 * A new box around the model at the centre of the view and the layers that continue it, turned
 * with their tiles. Not cut to the view: nothing the user sees is clipped when it opens. Null
 * without a model in view.
 */
export const placeBox = (viewer: Viewer, tilesets: Cesium3DTileset[]): ClipBox | null => {
	const candidates = boxCandidates(tilesets);
	const anchor = findAnchor(viewer, candidates);
	if (!anchor) return null;

	const roots = candidates.map((tileset) => tilesetVolume(tileset));
	const cluster = clusterAround(candidates.indexOf(anchor), roots, TOUCH_TOLERANCE);
	return fitBox(cluster.flatMap((index) => layerPieces(candidates[index])));
};

/**
 * Whether the user looks into the box: the scene point under the centre lies in it, or the
 * centre ray reaches it at most one box diagonal behind that point (a box built to look past the
 * surface in front of it), or, with nothing under the centre, at all.
 */
export const isSavedBoxLookedInto = (viewer: Viewer, box: ClipBox) => {
	const { scene, camera } = viewer;
	// Reads the depth of the last frame; the panel asks one frame after opening, with no box shown.
	const picked = scene.pickPositionSupported ? scene.pickPosition(canvasCentre(viewer)) : undefined;
	if (picked && pointInBox(box, picked)) return true;
	const entry = rayEntryDistance(box, camera.positionWC, camera.directionWC);
	if (entry === undefined) return false;
	if (!picked) return true;
	return entry <= Cartesian3.distance(camera.positionWC, picked) + 2 * boxBoundingSphere(box).radius;
};
